import { RegionShared } from "@1games-ru/region-shared";
import { RpcMpProviderFailedResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-failed-response.ts";
import { RpcMpProviderSuccessResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-success-response.ts";
import RpcMpProvider from "@/core/rpc-mp-provider/rpc-mp-provider.ts";

export default class AccountMpProvider {
  public static async getSocialClub(): Promise<
    RpcMpProviderFailedResponse | RpcMpProviderSuccessResponse<{ socialClub: string }>
  > {
    return RpcMpProvider.getInstance().callServer<undefined, { socialClub: string }>(
      RegionShared.Backend.Events.Account.GetSocialClub,
      undefined,
    );
  }

  public static async getEnableTfaMethods(): Promise<
    | RpcMpProviderFailedResponse
    | RpcMpProviderSuccessResponse<typeof RegionShared.Backend.Responses.TwoFactorStateResponse>
  > {
    return RpcMpProvider.getInstance().callServer<
      undefined,
      typeof RegionShared.Backend.Responses.TwoFactorStateResponse
    >(RegionShared.Backend.Events.Auth.TwoFactorState, undefined);
  }

  public static async getAccountSettings(): Promise<
    | RpcMpProviderFailedResponse
    | RpcMpProviderSuccessResponse<typeof RegionShared.Backend.Responses.GetAccountSettingsResponse>
  > {
    return RpcMpProvider.getInstance().callServer<
      undefined,
      typeof RegionShared.Responses.GetAccountSettingsResponse
    >(RegionShared.Backend.Events.Account.GetAccountSettings, undefined);
  }

  public static async updateSettings(
    data: typeof RegionShared.Backend.Requests.SettingsUpdateRequest,
  ): Promise<
    | RpcMpProviderFailedResponse
    | RpcMpProviderSuccessResponse<typeof RegionShared.Backend.Responses.SettingsUpdateResponse>
  > {
    return RpcMpProvider.getInstance().callServer<
      typeof RegionShared.Backend.Requests.SettingsUpdateRequest,
      typeof RegionShared.Backend.Responses.SettingsUpdateResponse
    >(RegionShared.Backend.Events.Account.SettingsUpdate, data);
  }

  public static async setSoundSettings(
    data: Partial<typeof RegionShared.Requests.GetAccountSettingsRequest>,
  ): Promise<
    | RpcMpProviderFailedResponse
    | RpcMpProviderSuccessResponse<typeof RegionShared.Responses.SettingsUpdateSoundResponse>
  > {
    return RpcMpProvider.getInstance().callServer<
      Partial<typeof RegionShared.Requests.GetAccountSettingsRequest>,
      typeof RegionShared.Responses.SettingsUpdateSoundResponse
    >(RegionShared.Backend.Events.Account.SettingsUpdateSound, data);
  }

  public static async login(
    password: string,
  ): Promise<
    | RpcMpProviderFailedResponse
    | RpcMpProviderSuccessResponse<typeof RegionShared.Responses.AuthResponse>
  > {
    return RpcMpProvider.getInstance().callServer<
      typeof RegionShared.Requests.AuthLoginRequest,
      typeof RegionShared.Responses.AuthResponse
    >(RegionShared.Backend.Events.Auth.Login, { password });
  }

  public static async logout(): Promise<
    RpcMpProviderFailedResponse | RpcMpProviderSuccessResponse<unknown>
  > {
    return RpcMpProvider.getInstance().callServer<undefined, unknown>(
      RegionShared.Backend.Events.Account.SignOut,
      undefined,
    );
  }

  public static async changeEmailSendCodeOld(
    data: typeof RegionShared.Requests.SettingsChangeEmailSendOldRequest,
  ): Promise<
    | RpcMpProviderFailedResponse
    | RpcMpProviderSuccessResponse<typeof RegionShared.Responses.SettingsChangeEmailSendOldResponse>
  > {
    return RpcMpProvider.getInstance().callServer<
      typeof RegionShared.Requests.SettingsChangeEmailSendOldRequest,
      typeof RegionShared.Responses.SettingsChangeEmailSendOldResponse
    >(RegionShared.Backend.Events.Account.SettingsChangeEmailSendOld, data);
  }

  public static async changeEmailSendCodeNew(
    data: typeof RegionShared.Requests.SettingsChangeEmailSendNewRequest,
  ): Promise<
    | RpcMpProviderFailedResponse
    | RpcMpProviderSuccessResponse<typeof RegionShared.Responses.SettingsChangeEmailSendNewResponse>
  > {
    return RpcMpProvider.getInstance().callServer<
      typeof RegionShared.Requests.SettingsChangeEmailSendNewRequest,
      typeof RegionShared.Responses.SettingsChangeEmailSendNewResponse
    >(RegionShared.Backend.Events.Account.SettingsChangeEmailSendNew, data);
  }

  public static async changeEmailCheckCodeNew(
    data: typeof RegionShared.Requests.SettingsChangeEmailCheckRequest,
  ): Promise<
    | RpcMpProviderFailedResponse
    | RpcMpProviderSuccessResponse<typeof RegionShared.Responses.SettingsChangeEmailCheckResponse>
  > {
    return RpcMpProvider.getInstance().callServer<
      typeof RegionShared.Requests.SettingsChangeEmailCheckRequest,
      typeof RegionShared.Responses.SettingsChangeEmailCheckResponse
    >(RegionShared.Backend.Events.Account.SettingsChangeEmailCheck, data);
  }

  public static async changePasswordSendCode(
    data: typeof RegionShared.Requests.SettingsChangePasswordSendRequest,
  ): Promise<
    | RpcMpProviderFailedResponse
    | RpcMpProviderSuccessResponse<typeof RegionShared.Responses.SettingsChangePasswordSendResponse>
  > {
    return RpcMpProvider.getInstance().callServer<
      typeof RegionShared.Requests.SettingsChangePasswordSendRequest,
      typeof RegionShared.Responses.SettingsChangePasswordSendResponse
    >(RegionShared.Backend.Events.Account.SettingsChangePasswordSend, data);
  }

  public static async changePasswordCheckCode(
    data: typeof RegionShared.Backend.Requests.SettingsChangePasswordCheckRequest,
  ): Promise<
    | RpcMpProviderFailedResponse
    | RpcMpProviderSuccessResponse<
        typeof RegionShared.Backend.Responses.SettingsChangePasswordCheckResponse
      >
  > {
    return RpcMpProvider.getInstance().callServer<
      typeof RegionShared.Backend.Requests.SettingsChangePasswordCheckRequest,
      typeof RegionShared.Backend.Responses.SettingsChangePasswordCheckResponse
    >(RegionShared.Backend.Events.Account.SettingsChangePasswordCheck, data);
  }

  public static async changePassword(
    data: typeof RegionShared.Backend.Requests.SettingsChangePasswordNewPasswordRequest,
  ): Promise<
    | RpcMpProviderFailedResponse
    | RpcMpProviderSuccessResponse<
        typeof RegionShared.Responses.SettingsChangePasswordNewPasswordResponse
      >
  > {
    return RpcMpProvider.getInstance().callServer<
      typeof RegionShared.Backend.Requests.SettingsChangePasswordNewPasswordRequest,
      typeof RegionShared.Backend.Responses.SettingsChangePasswordNewPasswordResponse
    >(RegionShared.Backend.Events.Account.SettingsChangePasswordNewPassword, data);
  }

  public static async getAllActiveSessions(): Promise<
    | RpcMpProviderFailedResponse
    | RpcMpProviderSuccessResponse<typeof RegionShared.Responses.GetAccountSessionsResponse>
  > {
    return RpcMpProvider.getInstance().callServer<
      undefined,
      typeof RegionShared.Backend.Responses.GetAccountSessionsResponse
    >(RegionShared.Backend.Events.Account.GetActiveSessions, undefined);
  }

  public static async closeAllActiveSessions(): Promise<
    | RpcMpProviderFailedResponse
    | RpcMpProviderSuccessResponse<typeof RegionShared.Responses.CloseAllActiveSessionsResponse>
  > {
    return RpcMpProvider.getInstance().callServer<
      undefined,
      typeof RegionShared.Backend.Responses.CloseAllActiveSessionsResponse
    >(RegionShared.Backend.Events.Account.CloseActiveSessions, undefined);
  }

  public static async getUser(): Promise<
    | RpcMpProviderFailedResponse
    | RpcMpProviderSuccessResponse<typeof RegionShared.Responses.GetUserResponse>
  > {
    return RpcMpProvider.getInstance().callServer<
      undefined,
      typeof RegionShared.Backend.Responses.GetUserResponse
    >(RegionShared.Backend.Events.Account.GetUser, undefined);
  }

  public static async getCharacters(): Promise<
    | RpcMpProviderFailedResponse
    | RpcMpProviderSuccessResponse<typeof RegionShared.Responses.GetCharactersResponse>
  > {
    return RpcMpProvider.getInstance().callServer<
      undefined,
      typeof RegionShared.Backend.Responses.GetCharactersResponse
    >(RegionShared.Backend.Events.Account.GetCharacters, undefined);
  }

  public static async deleteCharacter(
    data: typeof RegionShared.Backend.Requests.CharacterDestroyRequest,
  ): Promise<
    | RpcMpProviderFailedResponse
    | RpcMpProviderSuccessResponse<typeof RegionShared.Backend.Responses.CharacterDestroyResponse>
  > {
    return RpcMpProvider.getInstance().callServer<
      typeof RegionShared.Backend.Requests.CharacterDestroyRequest,
      typeof RegionShared.Backend.Responses.CharacterDestroyResponse
    >(RegionShared.Backend.Events.Character.Destroy, data);
  }

  public static async restoreCharacter(
    data: typeof RegionShared.Backend.Requests.CharacterRestoreRequest,
  ): Promise<
    | RpcMpProviderFailedResponse
    | RpcMpProviderSuccessResponse<typeof RegionShared.Backend.Responses.CharacterRestoreResponse>
  > {
    return RpcMpProvider.getInstance().callServer<
      typeof RegionShared.Backend.Requests.CharacterRestoreRequest,
      typeof RegionShared.Backend.Responses.CharacterRestoreResponse
    >(RegionShared.Backend.Events.Character.Restore, data);
  }

  public static async getCodeTimer(
    data: typeof RegionShared.Requests.GetThrottleStateRequest,
  ): Promise<
    | RpcMpProviderFailedResponse
    | RpcMpProviderSuccessResponse<typeof RegionShared.Responses.GetThrottleStateResponse>
  > {
    return RpcMpProvider.getInstance().callServer<
      typeof RegionShared.Backend.Requests.GetThrottleStateRequest,
      typeof RegionShared.Backend.Responses.GetThrottleStateResponse
    >(RegionShared.Backend.Events.Account.GetThrottleState, data);
  }

  public static async buySlot(): Promise<
    RpcMpProviderFailedResponse | RpcMpProviderSuccessResponse<unknown>
  > {
    return RpcMpProvider.getInstance().callServer<unknown, unknown>(
      RegionShared.Backend.Events.Donate.BuyCharacterSlot,
      undefined,
    );
  }

  public static async enter(
    characterId: string,
  ): Promise<RpcMpProviderFailedResponse | RpcMpProviderSuccessResponse<unknown>> {
    return RpcMpProvider.getInstance().callServer<{ characterId: string }, unknown>(
      RegionShared.Backend.Events.Character.Select,
      { characterId },
    );
  }

  public static async createCharacter(): Promise<
    RpcMpProviderFailedResponse | RpcMpProviderSuccessResponse<unknown>
  > {
    console.log(RegionShared.Backend.Events.Character.CreateStart);
    return RpcMpProvider.getInstance().callServer<unknown, unknown>(
      RegionShared.Backend.Events.Character.CreateStart,
      undefined,
    );
  }
}
